import React, { useMemo, useCallback, useState } from 'react';

import { Icon } from 'js/components';

import SourceCard from './SourceCard';

import { faCalendarAlt } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';

import styles from './AssessUserGroup.module.scss';
import { useStandardCollator } from 'js/utils/string';

function RecentCard({
  assessment,
  onClick = () => {},
  maxCapabilities = 3,
  maxElements = 3,
}) {
  const collator = useStandardCollator();

  const name = useMemo(
    () =>
      assessment.capability_sets.sort(
        (a, b) =>
          collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
      )[0]?.name,
    [assessment.capability_sets, collator]
  );

  const sortedCapabilities = useMemo(
    () =>
      assessment.capabilities
        .sort(
          (a, b) =>
            collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
        )
        .slice(0, maxCapabilities),
    [assessment.capabilities, maxCapabilities, collator]
  );

  const capabilityList = useMemo(
    () => sortedCapabilities.map((capability) => capability.name).join(', '),
    [sortedCapabilities]
  );

  const capabilityElements = useMemo(
    () =>
      Object.values(assessment?.assessment?.document?.capabilityElements ?? {}),
    [assessment]
  );

  const [isLoading, setIsLoading] = useState(false);
  const handleClick = useCallback(async () => {
    setIsLoading(true);
    try {
      await onClick(assessment.id);
    } catch (_err) {
      setIsLoading(false);
    }
  }, [assessment.id, onClick]);

  return (
    <SourceCard
      title={name ?? 'Recent assessment'}
      superscript={
        assessment.capability_sets.length > 1
          ? `+${assessment.capability_sets.length - 1}`
          : undefined
      }
      onClick={handleClick}
      isLoading={isLoading}
    >
      <span className={styles['recent-content']}>
        <span className={styles['recent-capabilities']}>
          {capabilityList}
          {sortedCapabilities.length > maxCapabilities && (
            <span>+ {sortedCapabilities.length - maxCapabilities} more...</span>
          )}
        </span>
        <span className={styles['recent-body-footer']}>
          <span className={styles['recent-last-run']}>
            <label>Last run on</label>
            <Icon
              icon={faCalendarAlt}
              size="lg"
              className="text-info icon-left"
            />
            {format(assessment.went_live_at, 'PP')}
          </span>
          <span className={styles['recent-capability-elements']}>
            {capabilityElements.length > maxElements ? (
              <>
                {capabilityElements.slice(0, maxElements - 1).map((ce) => (
                  <span
                    key={ce.guid}
                    className={styles['recent-capability-element']}
                  >
                    {ce.name}
                  </span>
                ))}
                <span className={styles['recent-capability-element']}>
                  + {capabilityElements.length - (maxElements - 1)} more
                </span>
              </>
            ) : (
              capabilityElements.map((ce) => (
                <span
                  key={ce.guid}
                  className={styles['recent-capability-element']}
                >
                  {ce.name}
                </span>
              ))
            )}
          </span>
        </span>
      </span>
    </SourceCard>
  );
}

export default RecentCard;
